import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import ClientCard from './ClientCard'
import EstimateDialog from './EstimateDialog'
import { LOCKED_CONFIRM, isLocked } from '../pages/Journal'
import { useAuth } from '../auth'
import { formatHM, fromLocalInput, toLocalInput, todayISO } from '../lib/format'
import type { PaymentStatus, Priority, Task, TimeEntry, WorkStatus } from '../types'
import { PRIORITY_LABEL, WORK_STATUS_LABEL } from '../types'
import { useEscClose } from '../lib/useEscClose'

interface Props {
  task: Task | null // null -> создание
  onClose: () => void
  /** Открыть заявку, из которой выросла задача. Не задан — ссылку не рисуем:
   *  в самой карточке заявки возвращаться уже некуда. */
  onOpenTicket?: (ticketId: number) => void
}

interface FormState {
  title: string
  description: string
  client_id: string
  subclient_id: string
  project_id: string
  source_id: string
  assignee_id: string
  work_status: WorkStatus
  payment_status: PaymentStatus
  priority: Priority
  is_support: boolean
  work_date: string
  due_date: string
  rate_override: string
  fixed_amount: string
}

function initial(task: Task | null, userId: number): FormState {
  return {
    title: task?.title ?? '',
    description: task?.description ?? '',
    client_id: task ? String(task.client_id) : '',
    subclient_id: task?.subclient_id ? String(task.subclient_id) : '',
    project_id: task?.project_id ? String(task.project_id) : '',
    source_id: task?.source_id ? String(task.source_id) : '',
    assignee_id: String(task?.assignee_id ?? userId),
    work_status: task?.work_status ?? 'new',
    payment_status: task?.payment_status ?? 'to_pay',
    priority: task?.priority ?? 'normal',
    is_support: task?.is_support ?? false,
    work_date: task?.work_date ?? todayISO(),
    due_date: task?.due_date ?? '',
    rate_override: task?.rate_override ?? '',
    fixed_amount: task?.fixed_amount ?? '',
  }
}

export default function TaskDialog({ task: initialTask, onClose, onOpenTicket }: Props) {
  /* isAdmin здесь остаётся только для источника задачи: его сервер закрывает
     именно на администратора. Правка задач из ведомости — отдельное право. */
  const { user, canSeeAll, isAdmin, can } = useAuth()
  const mayEditLocked = can('tasks.edit_locked')
  const qc = useQueryClient()

  // Задача, созданная прямо из этой формы: карточка не закрывается, а переходит
  // в режим редактирования — иначе после «сохранить и оценить» некуда возвращаться.
  const [createdTask, setCreatedTask] = useState<Task | null>(null)
  const baseTask = initialTask ?? createdTask

  // Открытая карточка должна видеть свои же правки интервалов и оценки. Список
  // задач в журнале — снимок на момент открытия, поэтому тянем задачу отдельно.
  const live = useQuery({
    queryKey: ['task', baseTask?.id],
    queryFn: () => api.task(baseTask!.id),
    enabled: !!baseTask,
    initialData: baseTask ?? undefined,
    staleTime: 0,
  })
  const task = baseTask ? live.data ?? baseTask : null

  const [form, setForm] = useState<FormState>(() => initial(initialTask, user!.id))
  const [error, setError] = useState('')
  const [estimateOpen, setEstimateOpen] = useState(false)
  // Оценку просили до сохранения — открываем её сразу после успешного сохранения
  const [pendingEstimate, setPendingEstimate] = useState(false)
  // Согласие править закрытую задачу: спрашиваем один раз за открытие карточки
  const [lockConfirmed, setLockConfirmed] = useState(false)
  // Доступы и контакты клиента прямо из задачи: специалист открывает задачу
  // и ему сразу нужны учётки, а не поиск клиента в другом разделе
  const [clientTab, setClientTab] = useState<'accesses' | 'contacts' | null>(null)

  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.clients() })
  const sources = useQuery({ queryKey: ['sources'], queryFn: () => api.sources() })
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users(), enabled: canSeeAll })

  const clientId = form.client_id ? Number(form.client_id) : undefined
  const selectedClient = clients.data?.find((c) => c.id === clientId)
  const clientDefaultSource = selectedClient?.source_id ?? null
  // Проект показываем, только если в карточке клиента отмечена проектная работа
  const clientHasProjects = selectedClient?.has_projects ?? false
  const subclients = useQuery({
    queryKey: ['subclients', clientId],
    queryFn: () => api.subclients(clientId),
    enabled: !!clientId,
  })
  const projects = useQuery({
    queryKey: ['projects', clientId],
    queryFn: () => api.projects({ client_id: clientId }),
    enabled: !!clientId && clientHasProjects,
  })

  /* Объект показываем, только если он у клиента заведён. Пустой список — это
     не выбор из ничего, а лишнее поле, которое отвлекает в самом частом случае:
     у большинства клиентов объектов нет вовсе. */
  const clientHasSubclients = (subclients.data?.length ?? 0) > 0

  // Проекты, привязанные к другому подклиенту, показывать нельзя — сервер их отвергнет
  const visibleProjects = useMemo(() => {
    const list = projects.data ?? []
    if (!form.subclient_id) return list
    return list.filter(
      (p) => p.subclient_id === null || String(p.subclient_id) === form.subclient_id,
    )
  }, [projects.data, form.subclient_id])

  useEffect(() => {
    if (!clientHasProjects && form.project_id) {
      setForm((f) => ({ ...f, project_id: '' }))
      return
    }
    if (form.project_id && !visibleProjects.some((p) => String(p.id) === form.project_id)) {
      setForm((f) => ({ ...f, project_id: '' }))
    }
  }, [visibleProjects, form.project_id, clientHasProjects])

  // Новая задача наследует источник из карточки клиента. У существующей задачи
  // значение уже своё — его подменять нельзя.
  useEffect(() => {
    if (task || !clientDefaultSource) return
    setForm((f) => (f.source_id ? f : { ...f, source_id: String(clientDefaultSource) }))
  }, [clientDefaultSource, task])

  // Смена источника у сохранённой задачи — операция администратора: она правит
  // и справочник клиента, чтобы следующие задачи не унаследовали старое значение.
  const sourceChanged = !!task && String(task.source_id ?? '') !== form.source_id
  const [syncClientSource, setSyncClientSource] = useState(true)

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        client_id: Number(form.client_id),
        subclient_id: form.subclient_id ? Number(form.subclient_id) : null,
        project_id: form.project_id ? Number(form.project_id) : null,
        source_id: Number(form.source_id),
        work_status: form.work_status,
        payment_status: form.payment_status,
        priority: form.priority,
        is_support: form.is_support,
        work_date: form.work_date,
        due_date: form.due_date || null,
        rate_override: form.rate_override ? Number(form.rate_override) : null,
        fixed_amount: form.fixed_amount ? Number(form.fixed_amount) : null,
      }
      if (canSeeAll) body.assignee_id = Number(form.assignee_id)
      if (task && sourceChanged) body.update_client_default_source = syncClientSource
      return task ? api.updateTask(task.id, body, lockConfirmed) : api.createTask(body)
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['task'] })
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['kpi'] })
      qc.invalidateQueries({ queryKey: ['running'] })
      if (pendingEstimate) {
        setPendingEstimate(false)
        setCreatedTask(saved)
        setEstimateOpen(true)
        return // карточку оставляем открытой — сейчас поверх неё встанет окно оценки
      }
      onClose()
    },
    onError: (e: any) => {
      setPendingEstimate(false)
      setError(e?.message ?? 'Не удалось сохранить')
    },
  })

  function validate(): boolean {
    setError('')
    if (!form.title.trim()) {
      setError('Укажите тему задачи')
      return false
    }
    if (!form.client_id) {
      setError('Выберите клиента')
      return false
    }
    if (!form.source_id) {
      setError('Выберите источник задачи')
      return false
    }
    return true
  }

  /** Закрытую задачу правит только администратор и только с подтверждением. */
  function ensureUnlocked(): boolean {
    if (!task || !isLocked(task)) return true
    if (!mayEditLocked) {
      setError('Задача в ведомости или выплачена — у вас нет права её менять')
      return false
    }
    if (lockConfirmed) return true
    if (!confirm(LOCKED_CONFIRM)) return false
    setLockConfirmed(true)
    return true
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    if (!ensureUnlocked()) return
    save.mutate()
  }

  /** Оценка живёт у сохранённой задачи. Новую сначала предлагаем сохранить. */
  function requestEstimate() {
    if (task) {
      if (!ensureUnlocked()) return
      setEstimateOpen(true)
      return
    }
    if (!validate()) return
    if (!ensureUnlocked()) return
    if (!confirm('Для оценки нужно сохранить задачу. Сохранить?')) return
    setPendingEstimate(true)
    save.mutate()
  }

  useEscClose(onClose)

  return (
    <>
      <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <form className={`modal${task ? ' huge' : ''} tdw`} onSubmit={submit}>
          <div className="tdw-head">
            <div className="tdw-titles">
              <div className="tdw-num">
                {task ? `Задача № ${task.id}` : 'Новая задача'}
                {task && (
                  <>
                    <span className={`badge ${task.work_status}`}>
                      {WORK_STATUS_LABEL[task.work_status]}
                    </span>
                    {task.is_support && <span className="support-chip">Сопровождение</span>}
                    {isLocked(task) && <span className="tkw-alarm">правки закрыты</span>}
                  </>
                )}
              </div>
              <div className="tdw-subject">{form.title || 'Без темы'}</div>
              {/* Откуда задача взялась. Без этой строки специалист видит голое
                  задание и не знает, что за ним стоит разговор с клиентом. */}
              {task?.ticket_id != null && (
                <div className="tdw-from">
                  Из записи:{' '}
                  {onOpenTicket ? (
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => onOpenTicket(task.ticket_id!)}
                    >
                      {task.ticket_title} № {task.ticket_id}
                      {task.ticket_subject ? ` — ${task.ticket_subject}` : ''}
                    </button>
                  ) : (
                    <b>
                      {task.ticket_title} № {task.ticket_id}
                      {task.ticket_subject ? ` — ${task.ticket_subject}` : ''}
                    </b>
                  )}
                  {task.ticket_category_title && (
                    <span className="tk-cat-chip">{task.ticket_category_title}</span>
                  )}
                </div>
              )}
            </div>
            <button type="button" className="tkw-close" title="Закрыть окно" onClick={onClose}>
              ✕
            </button>
          </div>

          <div className="modal-body">
            {error && <div className="error">{error}</div>}
          {task && isLocked(task) && (
            <div className="warn">
              {task.payment_status === 'paid'
                ? 'Задача выплачена'
                : 'Задача включена в ведомость'}
              . Суммы по ней уже посчитаны, поэтому правки закрыты
              {mayEditLocked
                ? '. У вас есть право на правку закрытых задач — программа переспросит.'
                : '. Обратитесь к администратору.'}
            </div>
          )}

          <div className={task ? 'tdw-body' : undefined}>
          <div className="grid-2 tdw-col">
            <div className="field full">
              <label>Тема</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Настройка нового сканера"
                autoFocus
                required
              />
            </div>

            <div className="field full">
              <label>Описание</label>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div className="form-group-title">Для кого и откуда</div>

            {/* Клиент и кнопки к его карточке — одной строкой: имя выбрано в
                списке рядом, повторять его подписью было незачем. */}
            <div className="field full field-accent">
              <label>Клиент</label>
              <div className="field-with-actions">
                <select
                  value={form.client_id}
                  onChange={(e) =>
                    setForm({ ...form, client_id: e.target.value, subclient_id: '', project_id: '' })
                  }
                  required
                >
                  <option value="">— выберите —</option>
                  {clients.data?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {clientId && (
                  <>
                    <button type="button" className="chip-btn" onClick={() => setClientTab('accesses')}>
                      Доступы
                    </button>
                    <button type="button" className="chip-btn" onClick={() => setClientTab('contacts')}>
                      Контакты
                    </button>
                  </>
                )}
              </div>
            </div>

            {clientHasSubclients && (
              <div className="field">
                <label>Подклиент / объект</label>
                <select
                  value={form.subclient_id}
                  onChange={(e) => setForm({ ...form, subclient_id: e.target.value })}
                >
                  <option value="">—</option>
                  {subclients.data?.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {clientHasProjects && (
              <div className="field">
                <label>Проект</label>
                <select
                  value={form.project_id}
                  onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                >
                  <option value="">—</option>
                  {visibleProjects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="field">
              <label>Источник *</label>
              <select
                value={form.source_id}
                onChange={(e) => setForm({ ...form, source_id: e.target.value })}
                disabled={!isAdmin}
                required
              >
                <option value="">— из карточки клиента —</option>
                {sources.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              {!isAdmin && (
                <span className="hint-inline">
                  {clientDefaultSource
                    ? 'Берётся из карточки клиента. Изменить может администратор.'
                    : 'У клиента не задан источник — попросите администратора указать его.'}
                </span>
              )}
            </div>

            {isAdmin && sourceChanged && (
              <div className="field full highlight">
                <label>Источник изменён</label>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={syncClientSource}
                    onChange={(e) => setSyncClientSource(e.target.checked)}
                  />
                  <span>
                    Записать новый источник в карточку клиента как источник по умолчанию
                  </span>
                </label>
                <span className="hint-inline">
                  {syncClientSource
                    ? 'Следующие задачи этого клиента унаследуют исправленный источник.'
                    : 'Изменение затронет только эту задачу, справочник останется прежним.'}
                </span>
              </div>
            )}



            {/* Статус оплаты отсюда убран: им распоряжается расчёт выплат, и
                ручная правка в карточке расходилась с ведомостями. Значение
                хранится в форме и уходит на сервер неизменным. */}
            {canSeeAll && (
              <>
                <div className="form-group-title">Оплата</div>
              </>
            )}

            {canSeeAll && (
              <>
                <div className="field">
                  <label>Ставка, ₽/час (переопределить)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.rate_override}
                    placeholder="из справочника"
                    onChange={(e) => setForm({ ...form, rate_override: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Фикс за задачу, ₽</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.fixed_amount}
                    placeholder="вместо почасовой"
                    onChange={(e) => setForm({ ...form, fixed_amount: e.target.value })}
                  />
                </div>
              </>
            )}
          </div>

          <div className="grid-2 tdw-col">
            <div className="form-group-title">Как идёт работа</div>

            <div className="field">
              <label>Дата работ</label>
              <input
                type="date"
                value={form.work_date}
                onChange={(e) => setForm({ ...form, work_date: e.target.value })}
                required
              />
            </div>

            <div className="field">
              <label>Срок</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              />
              <span className="hint-inline">К какому дню обещали</span>
            </div>

            <div className="field">
              <label>Статус работы</label>
              <select
                value={form.work_status}
                onChange={(e) => setForm({ ...form, work_status: e.target.value as WorkStatus })}
              >
                {Object.entries(WORK_STATUS_LABEL).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className={`field field-priority prio-${form.priority}`}>
              <label>Срочность</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value as Priority })}
              >
                {Object.entries(PRIORITY_LABEL).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {canSeeAll && (
              <div className="field">
                <label>Исполнитель</label>
                <select
                  value={form.assignee_id}
                  onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}
                >
                  {users.data?.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Сопровождение стоит рядом с оценкой: оно её и отменяет. Оценка
                нужна, чтобы посчитать выплату, а по абонентскому договору
                платить нечего — поэтому под галочкой она гаснет. */}
            <div className="field field-support">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={form.is_support}
                  onChange={(e) => setForm({ ...form, is_support: e.target.checked })}
                />
                <span>
                  <b>Сопровождение</b>
                </span>
              </label>
              <span className="hint-inline">
                {form.is_support
                  ? 'Время учитывается, в ведомость задача не попадёт.'
                  : 'Работа по абонентскому договору, отдельно не оплачивается.'}
              </span>
            </div>

            <div
              className={`field field-accent${
                form.is_support ? ' field-off' : task?.estimate == null ? ' field-warn' : ''
              }`}
            >
              <label>Оценка, ч</label>
              <button
                type="button"
                className="btn"
                onClick={requestEstimate}
                disabled={save.isPending || form.is_support}
              >
                {task?.estimate != null ? `${Number(task.estimate)} ч — изменить` : 'Оценить'}
              </button>
              <span className="hint-inline">
                {form.is_support
                  ? 'По сопровождению оценка не нужна — выплаты по задаче не будет'
                  : task
                    ? 'Складывается из строк работ в отдельном окне'
                    : 'Задача будет сохранена, затем откроется окно оценки'}
              </span>
            </div>

          </div>
          </div>

            {task && <EstimateHistory task={task} />}
            {task && <EntriesEditor task={task} />}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn" onClick={onClose}>
              Отмена
            </button>
            <button className="btn primary" disabled={save.isPending}>
              {save.isPending ? 'Сохраняем…' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>

      {/* Окно оценки — соседний узел, а не потомок формы задачи: вложенная форма
          вызывает нативную отправку и перезагружает страницу. */}
      {clientTab && clientId && (
        <ClientCard
          clientId={clientId}
          clientName={selectedClient?.name ?? ''}
          tab={clientTab}
          onClose={() => setClientTab(null)}
        />
      )}

      {estimateOpen && task && (
        <EstimateDialog
          task={task}
          confirmLocked={lockConfirmed}
          onClose={() => setEstimateOpen(false)}
        />
      )}
    </>
  )
}

/** Строка интервала: просмотр и правка начала/конца на месте. */
function EntryRow({
  taskId,
  entry,
  onDone,
  onError,
  onRemove,
}: {
  taskId: number
  entry: TimeEntry
  onDone: () => void
  onError: (msg: string) => void
  onRemove: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const running = entry.stopped_at === null

  const save = useMutation({
    mutationFn: () =>
      api.updateEntry(taskId, entry.id, {
        started_at: fromLocalInput(from),
        // у идущего интервала конец не трогаем — его закроет кнопка «Стоп»
        ...(running ? {} : { stopped_at: fromLocalInput(to) }),
      }),
    onSuccess: () => {
      setEditing(false)
      onError('')
      onDone()
    },
    onError: (e: any) => onError(e?.message ?? 'Не удалось изменить интервал'),
  })

  function startEdit() {
    setFrom(toLocalInput(entry.started_at))
    setTo(toLocalInput(entry.stopped_at))
    setEditing(true)
  }

  if (editing) {
    return (
      <tr>
        <td>
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={{ width: '100%' }}
          />
        </td>
        <td>
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            disabled={running}
            style={{ width: '100%' }}
          />
        </td>
        <td className="num mono">—</td>
        <td>правка</td>
        <td>
          <button
            type="button"
            className="btn small primary"
            disabled={!from || (!running && !to) || save.isPending}
            onClick={() => save.mutate()}
          >
            Сохранить
          </button>{' '}
          <button type="button" className="btn small" onClick={() => setEditing(false)}>
            Отмена
          </button>
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td className="mono">{new Date(entry.started_at).toLocaleString('ru-RU')}</td>
      <td className="mono">
        {entry.stopped_at ? new Date(entry.stopped_at).toLocaleString('ru-RU') : '— идёт —'}
      </td>
      <td className="num mono">{formatHM(entry.seconds)}</td>
      <td>
        {entry.source === 'timer' ? 'таймер' : entry.source === 'manual' ? 'вручную' : 'импорт'}
      </td>
      <td>
        <button type="button" className="btn small" onClick={startEdit}>
          Изменить
        </button>{' '}
        <button type="button" className="btn small danger" onClick={onRemove}>
          ✕
        </button>
      </td>
    </tr>
  )
}


/** История правок оценки: кто, когда, с какого значения на какое и почему. */
function EstimateHistory({ task }: { task: Task }) {
  if (task.estimate_changes.length === 0) return null

  const show = (v: string | null) => (v === null ? '—' : String(Number(v)))

  return (
    <div className="section" style={{ marginTop: 18 }}>
      <h2>История оценки</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Когда</th>
              <th>Кто</th>
              <th className="num">Было</th>
              <th className="num">Стало</th>
              <th>Состав</th>
              <th>Комментарий</th>
            </tr>
          </thead>
          <tbody>
            {task.estimate_changes.map((ch) => (
              <tr key={ch.id}>
                <td className="mono">{new Date(ch.changed_at).toLocaleString('ru-RU')}</td>
                <td>{ch.user_name ?? '—'}</td>
                <td className="num mono">{show(ch.old_value)}</td>
                <td className="num mono">{show(ch.new_value)}</td>
                <td className="wrap">{ch.snapshot ?? '—'}</td>
                <td className="wrap">{ch.comment ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Ручная правка интервалов — замена «дописыванию времени в ячейку» из Excel. */
function EntriesEditor({ task }: { task: Task }) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [error, setError] = useState('')

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['task'] })
    qc.invalidateQueries({ queryKey: ['tasks'] })
    qc.invalidateQueries({ queryKey: ['kpi'] })
  }

  const add = useMutation({
    mutationFn: () =>
      api.addEntry(task.id, { started_at: fromLocalInput(from), stopped_at: fromLocalInput(to) }),
    onSuccess: () => {
      setAdding(false)
      setFrom('')
      setTo('')
      setError('')
      refresh()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось добавить интервал'),
  })

  const remove = useMutation({
    mutationFn: (entryId: number) => api.deleteEntry(task.id, entryId),
    onSuccess: refresh,
  })

  return (
    <div className="section" style={{ marginTop: 18 }}>
      <h2>
        Интервалы работы · всего {formatHM(task.tracked_seconds)}
      </h2>
      {error && <div className="error">{error}</div>}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Начало</th>
              <th>Конец</th>
              <th className="num">Длительность</th>
              <th>Источник</th>
              <th style={{ width: 160 }} />
            </tr>
          </thead>
          <tbody>
            {task.entries.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">
                  Отметок ещё нет
                </td>
              </tr>
            )}
            {task.entries.map((e) => (
              <EntryRow
                key={e.id}
                taskId={task.id}
                entry={e}
                onDone={refresh}
                onError={setError}
                onRemove={() => remove.mutate(e.id)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {adding ? (
        <div className="toolbar" style={{ marginTop: 10 }}>
          <div className="field">
            <label>Начало</label>
            <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field">
            <label>Конец</label>
            <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button
            type="button"
            className="btn primary"
            disabled={!from || !to || add.isPending}
            onClick={() => add.mutate()}
          >
            Добавить
          </button>
          <button type="button" className="btn" onClick={() => setAdding(false)}>
            Отмена
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="btn small"
          style={{ marginTop: 10 }}
          onClick={() => {
            const now = toLocalInput(new Date().toISOString())
            setFrom(now)
            setTo(now)
            setAdding(true)
          }}
        >
          + Добавить интервал вручную
        </button>
      )}
    </div>
  )
}
